import { Injectable, inject, signal } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { currentDisplayMode, isStandaloneDisplay } from '../utils/pwa';

/**
 * RESPONSIBILITY: Owns the browser's PWA install-prompt lifecycle. Captures
 * the `beforeinstallprompt` event (Chromium fires it once installability
 * criteria are met — manifest + service worker + engagement), defers it, and
 * replays it from an explicit user action so the app controls when the
 * install dialog appears (never the mini-infobar). Also tracks the
 * `appinstalled` completion.
 *
 * SIGNALS (all readonly):
 *   canInstall — true while a deferred prompt is being held; drives the
 *                "Install app" affordance. Always false on browsers that
 *                never fire beforeinstallprompt (Safari, Firefox) and once
 *                the app is installed.
 *   installed  — true when running in an app window (standalone display
 *                mode) or after an appinstalled event this session.
 *
 * USED BY: App (root — constructed at bootstrap so the early
 *   beforeinstallprompt is never missed), ProfilePanelComponent (install row).
 *
 * AI CONTEXT:
 *   - beforeinstallprompt can fire BEFORE lazy surfaces exist, so this
 *     service must be instantiated at bootstrap (app.ts injects it).
 *   - prompt() may only be called once per captured event and only from a
 *     user gesture — promptInstall() is wired to a button click.
 */

/** Chromium's non-standard install-prompt event (not in TS DOM lib). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly analytics = inject(AnalyticsService);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  private readonly _canInstall = signal(false);
  readonly canInstall = this._canInstall.asReadonly();

  private readonly _installed = signal(isStandaloneDisplay());
  readonly installed = this._installed.asReadonly();

  constructor() {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeinstallprompt', (event) => {
      // Defer: suppress Chrome's own mini-infobar and hold the prompt for
      // the in-app "Install app" action.
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this._canInstall.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this._canInstall.set(false);
      this._installed.set(true);
      this.analytics.capture(ANALYTICS_EVENTS.PWA_INSTALLED, {
        display_mode: currentDisplayMode(),
      });
    });
  }

  /**
   * Replay the deferred install prompt. Must be called from a user gesture.
   * Resolves with the user's choice, or 'unavailable' when no prompt is held
   * (already installed, unsupported browser, or already consumed).
   */
  async promptInstall(
    source: 'profile-panel',
  ): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const prompt = this.deferredPrompt;
    if (!prompt) return 'unavailable';

    // A captured event's prompt() is single-use — drop it regardless of the
    // outcome (Chromium fires a fresh beforeinstallprompt after a dismissal
    // when it decides the user can be re-prompted).
    this.deferredPrompt = null;
    this._canInstall.set(false);

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      this.analytics.capture(ANALYTICS_EVENTS.PWA_INSTALL_PROMPTED, {
        outcome: choice.outcome,
        source,
      });
      return choice.outcome;
    } catch (err) {
      console.warn('[PwaInstall] Install prompt failed:', err);
      return 'unavailable';
    }
  }
}
